import { useEffect, useRef, useState } from 'react';
import { CircleNotch, MagicWand } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { normalizeRename } from '~/components/inline-rename-title';
import { Tip } from '~/components/ui/tip';
import { useRenameChat, useSuggestChatTitle } from '~/hooks/mutations/use-chat-mutations';
import { cn } from '~/lib/utils';

/**
 * The chat's name in the chat window's header crumb (eddy, 2026-10-26: "click
 * into the title and edit it, or a wizard icon to use AI to generate a short
 * title"). Click the name to type a new one — Enter or blur saves, Escape
 * cancels, same as the list rename. The wand asks Eddy for a short title read
 * from the conversation so far and saves it at once; the person can still edit
 * it afterwards. Both save through the one chat rename, so every surface that
 * shows the name (the window tab, the Chats list, search) follows.
 *
 * `sessionId` null means a chat that has no thread yet (nothing sent): the
 * name shows, but there is nothing to rename.
 *
 * The wand shows only while the name is being edited (eddy, 2026-10-28), next
 * to the field; while a suggestion is on its way its spinner stays.
 */
export function ChatWindowTitle({
  sessionId,
  title,
  initialEditing = false,
}: {
  sessionId: string | null;
  title: string;
  /** Test seam: a static render has no clicks, so it opens the edit by seeding it. */
  initialEditing?: boolean;
}) {
  const rename = useRenameChat();
  const suggest = useSuggestChatTitle();
  const [editing, setEditing] = useState(initialEditing);
  const [text, setText] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipBlurRef = useRef(false);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const save = async (next: string, message: string) => {
    if (!sessionId) return;
    try {
      await rename.mutateAsync({ sessionId, title: next });
      toast.success(message);
    } catch (err) {
      toast.error((err as Error)?.message || 'Rename failed');
    }
  };

  const commit = async () => {
    setEditing(false);
    const next = normalizeRename(text, title);
    if (next !== null) await save(next, 'Chat renamed');
  };

  const generate = () => {
    if (!sessionId || suggest.isPending) return;
    suggest.mutate(sessionId, {
      onSuccess: ({ title: suggested }) => {
        if (normalizeRename(suggested, title) === null) {
          toast.message('That title already fits');
          return;
        }
        void save(suggested, `Renamed to “${suggested}”`);
      },
      onError: (err) => toast.error((err as Error)?.message || 'Could not suggest a title'),
    });
  };

  const busy = rename.isPending || suggest.isPending;

  const wand = (
    <Tip label={suggest.isPending ? 'Thinking of a title…' : 'Suggest a short title'}>
      <button
        type="button"
        data-testid="chat-window-title-suggest"
        // Keep focus in the field: a blur would close the edit and unmount
        // this button before its click lands.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          skipBlurRef.current = true;
          setEditing(false);
          generate();
        }}
        disabled={busy}
        aria-label="Suggest a short title with AI"
        className={cn(
          'inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none',
          suggest.isPending && 'text-primary',
        )}
      >
        {suggest.isPending ? <CircleNotch size={15} className="animate-spin" /> : <MagicWand size={15} />}
      </button>
    </Tip>
  );

  if (editing) {
    return (
      <span className="flex min-w-0 flex-1 items-center gap-0.5">
      <input
        ref={inputRef}
        data-testid="chat-window-title-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          } else if (e.key === 'Escape') {
            // Escape here cancels the edit only — never minimizes the window.
            e.preventDefault();
            e.stopPropagation();
            skipBlurRef.current = true;
            setEditing(false);
          }
        }}
        onBlur={() => {
          if (skipBlurRef.current) {
            skipBlurRef.current = false;
            return;
          }
          void commit();
        }}
        maxLength={200}
        aria-label="Chat name"
        className="min-w-[8rem] max-w-[28rem] flex-1 rounded-md border border-primary/40 bg-background px-1.5 py-0.5 text-[length:var(--text-md)] font-semibold tracking-[-0.2px] text-foreground outline-none focus:border-primary"
      />
      {sessionId && wand}
      </span>
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-0.5">
      {sessionId ? (
        <Tip label="Rename">
          <button
            type="button"
            data-testid="chat-window-title"
            onClick={() => {
              setText(title);
              setEditing(true);
            }}
            aria-label={`Rename ${title}`}
            className="min-w-0 truncate rounded-md px-1 py-0.5 text-left text-[length:var(--text-md)] font-semibold tracking-[-0.2px] text-foreground transition-colors hover:bg-muted"
          >
            {title}
          </button>
        </Tip>
      ) : (
        <span data-testid="chat-window-title" className="min-w-0 truncate px-1 text-[length:var(--text-md)] font-semibold tracking-[-0.2px] text-foreground">
          {title}
        </span>
      )}
      {/* Not editing: the wand only while its suggestion is on its way. */}
      {sessionId && suggest.isPending && (
        <span role="status" className="inline-flex size-7 shrink-0 items-center justify-center text-primary" aria-label="Thinking of a title…">
          <CircleNotch size={15} className="animate-spin" />
        </span>
      )}
    </span>
  );
}
